// src/pages/portal/CataloguesSection.js
import React, { useState, useEffect, useCallback } from 'react';
import { categoryService, translationService, catalogueOrderService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { ICON_KEYS, getCategoryIcon } from '../../constants/categoryIcons';
import IconPicker from '../../components/common/IconPicker';
import { renderEntityImage } from '../../utils/imageUtils';
import LimitReached from '../../components/portal/LimitReached';
import LockOverlay from '../../components/portal/LockOverlay';
import { atLimit } from '../../utils/featureGating';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Shared helpers ───────────────────────────────────────────────────────────
const inp = (extra = {}) => ({
    width: '100%', boxSizing: 'border-box',
    fontFamily: FONT, fontSize: 13, color: C.text,
    padding: '8px 12px', borderRadius: 8,
    border: `1.5px solid ${C.border}`, background: C.white,
    outline: 'none',
    ...extra,
});

const SubLabel = ({ children, mt = 24 }) => (
    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.muted, marginBottom: 8, marginTop: mt }}>
        {children}
    </div>
);

const DelBtn = ({ onClick }) => (
    <button onClick={onClick} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: C.mutedLight, padding: '2px 4px',
        fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center',
        transition: 'color 0.15s',
    }}
        onMouseEnter={e => e.currentTarget.style.color = C.red}
        onMouseLeave={e => e.currentTarget.style.color = C.mutedLight}
    >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    </button>
);

// ─── Translate icon SVG ───────────────────────────────────────────────────────
const TranslateIcon = ({ size = 14, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 8l6 0" />
        <path d="M4 14c.8-2.4 2.5-5 4-6" />
        <path d="M8 14c-.8-2.4-2.5-5-4-6" />
        <path d="M13 4l3 8 3-8" />
        <path d="M13 12h6" />
    </svg>
);

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ icon, message, cta, onCta }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '48px 24px',
        background: C.bg, borderRadius: 12, border: `1.5px dashed ${C.border}`,
    }}>
        <div style={{ marginBottom: 12, color: C.mutedLight }}>{icon}</div>
        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.muted, marginBottom: 16 }}>{message}</div>
        {cta && (
            <button onClick={onCta} style={{
                padding: '8px 20px', borderRadius: 8, border: `1.5px dashed ${C.greenBorder}`,
                background: C.greenLight, cursor: 'pointer', fontFamily: FONT,
                fontSize: 13, fontWeight: 700, color: C.green,
            }}>
                {cta}
            </button>
        )}
    </div>
);

// ─── Translation Modal ───────────────────────────────────────────────────────
const TranslationModal = ({ name, currentMode, currentManual, onSave, onClose }) => {
    const { t } = useLanguage();
    const [mode, setMode] = useState(currentMode || 'translation');
    const [manualText, setManualText] = useState('');
    const [aiResult, setAiResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Parse existing manual translation
    useEffect(() => {
        if (currentManual) {
            try {
                const parsed = typeof currentManual === 'string' ? JSON.parse(currentManual) : currentManual;
                setManualText(parsed?.ar || '');
            } catch {
                setManualText(currentManual || '');
            }
        }
    }, [currentManual]);

    // Auto-translate when mode is translation or transliteration
    useEffect(() => {
        if ((mode === 'translation' || mode === 'transliteration') && name) {
            setLoading(true);
            translationService.translateProductName(name, 'category')
                .then(result => {
                    setAiResult(result);
                })
                .catch(() => setAiResult(null))
                .finally(() => setLoading(false));
        }
    }, [mode, name]);

    // Escape key
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = { translation_mode: mode };
            if (mode === 'manual') {
                data.manual_translation = JSON.stringify({ ar: manualText });
            }
            await onSave(data);
        } finally {
            setSaving(false);
        }
    };

    const rawResult = mode === 'translation' ? aiResult?.translation : aiResult?.transliteration;
    const displayResult = (rawResult && rawResult === name) ? null : rawResult;
    const apiKeyMissing = rawResult && rawResult === name;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.4)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div className="portal-modal-card" style={{
                background: C.white, borderRadius: 14, padding: 28, width: 420,
                maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text, margin: 0 }}>
                        {t('translate')}: {name}
                    </h3>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: C.muted,
                        padding: 4, display: 'flex',
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Mode selector */}
                <SubLabel mt={0}>{t('translation_mode')}</SubLabel>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {[
                        { key: 'translation', label: t('translation_ai') },
                        { key: 'transliteration', label: t('transliteration') },
                        { key: 'manual', label: t('manual') },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setMode(opt.key)}
                            style={{
                                flex: 1, padding: '8px 12px', borderRadius: 8,
                                border: `1.5px solid ${mode === opt.key ? C.green : C.border}`,
                                background: mode === opt.key ? C.greenLight : C.white,
                                color: mode === opt.key ? C.green : C.muted,
                                fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', transition: 'all 0.15s',
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {/* Result area */}
                {mode === 'manual' ? (
                    <div>
                        <SubLabel mt={0}>{t('arabic_translation')}</SubLabel>
                        <input
                            value={manualText}
                            onChange={e => setManualText(e.target.value)}
                            placeholder={t('enter_arabic_translation')}
                            dir="rtl"
                            style={inp({ marginBottom: 16 })}
                        />
                    </div>
                ) : (
                    <div style={{
                        padding: 16, borderRadius: 10,
                        background: C.bg, border: `1.5px solid ${C.border}`,
                        marginBottom: 16, minHeight: 48,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {loading ? (
                            <span style={{ fontFamily: FONT, fontSize: 13, color: C.muted }}>{t('translating')}...</span>
                        ) : displayResult ? (
                            <span style={{ fontFamily: FONT, fontSize: 18, color: C.text, direction: 'rtl' }}>
                                {displayResult}
                            </span>
                        ) : apiKeyMissing ? (
                            <span style={{ fontFamily: FONT, fontSize: 13, color: C.red }}>
                                {t('translation_unavailable_api_key')}
                            </span>
                        ) : (
                            <span style={{ fontFamily: FONT, fontSize: 13, color: C.mutedLight }}>
                                {t('no_translation_available')}
                            </span>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{
                        padding: '8px 18px', borderRadius: 8,
                        border: `1.5px solid ${C.border}`, background: C.white,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                    }}>{t('cancel')}</button>
                    <button onClick={handleSave} disabled={saving} style={{
                        padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: C.green, color: C.white,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        opacity: saving ? 0.7 : 1,
                    }}>{saving ? t('saving') + '...' : t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// ─── Confirm Delete Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onClose }) => {
    const { t } = useLanguage();

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.4)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div className="portal-modal-card" style={{
                background: C.white, borderRadius: 14, padding: 28, width: 380,
                maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text, margin: '0 0 12px' }}>
                    {t('confirm_delete')}
                </h3>
                <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: '0 0 20px', lineHeight: 1.55 }}>
                    {message}
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{
                        padding: '8px 18px', borderRadius: 8,
                        border: `1.5px solid ${C.border}`, background: C.white,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                    }}>{t('cancel')}</button>
                    <button onClick={onConfirm} style={{
                        padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: C.red, color: C.white,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 700,
                    }}>{t('delete')}</button>
                </div>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN: CataloguesSection
// ═════════════════════════════════════════════════════════════════════════════
const CataloguesSection = ({ config, onToast, onReload }) => {
    const { t } = useLanguage();
    const { hasFeature, getLimit, limits } = useAuth();
    const isMobile = useIsMobile();

    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subLoading, setSubLoading] = useState(false);

    // Edit state for active category
    const [editName, setEditName] = useState('');
    const [editIcon, setEditIcon] = useState('star');
    const [editImageUrl, setEditImageUrl] = useState(null);
    const [editKds, setEditKds] = useState(false);
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // Add category
    const [addingCategory, setAddingCategory] = useState(false);
    const [newCatName, setNewCatName] = useState('');

    // Add subcategory
    const [addingSub, setAddingSub] = useState(false);
    const [newSubName, setNewSubName] = useState('');

    // Edit subcategory
    const [editingSubId, setEditingSubId] = useState(null);
    const [editSubName, setEditSubName] = useState('');
    const [editSubImageUrl, setEditSubImageUrl] = useState(null);

    // Modals
    const [translateModal, setTranslateModal] = useState(null); // { type: 'category'|'subcategory', id, name, mode, manual }
    const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, name }

    // ── Feature gating ───────────────────────────────────────────────────────
    const catCap = getLimit('max_categories');
    const catsFull = atLimit(limits, 'max_categories', categories.length);
    const subsLocked = !hasFeature('subcategories');

    // ── Fetch categories ─────────────────────────────────────────────────────
    const fetchCategories = useCallback(async () => {
        try {
            const data = await categoryService.getAll();
            setCategories(data);
            if (data.length > 0 && !activeCategory) {
                setActiveCategory(data[0]);
            } else if (activeCategory) {
                // Refresh active category data
                const updated = data.find(c => c.id === activeCategory.id);
                if (updated) setActiveCategory(updated);
                else if (data.length > 0) setActiveCategory(data[0]);
                else setActiveCategory(null);
            }
        } catch {
            onToast('error', t('failed_to_load_categories'));
        } finally {
            setLoading(false);
        }
    }, [activeCategory, onToast, t]);

    useEffect(() => { fetchCategories(); }, []);

    // ── Fetch subcategories when active category changes ─────────────────────
    useEffect(() => {
        if (!activeCategory) { setSubcategories([]); return; }
        setSubLoading(true);
        categoryService.getSubcategories(activeCategory.id)
            .then(data => setSubcategories(data))
            .catch(() => setSubcategories([]))
            .finally(() => setSubLoading(false));
    }, [activeCategory]);

    // ── Sync edit fields when active category changes ────────────────────────
    useEffect(() => {
        if (activeCategory) {
            setEditName(activeCategory.name || '');
            setEditIcon(activeCategory.icon || 'star');
            setEditImageUrl(activeCategory.image_url || null);
            setEditKds(activeCategory.is_kitchen_display || false);
        }
    }, [activeCategory]);

    // ── Category CRUD ────────────────────────────────────────────────────────
    const handleAddCategory = async () => {
        const name = newCatName.trim();
        if (!name) return;
        try {
            const created = await categoryService.create({ name, icon: 'star', is_kitchen_display: false });
            setCategories(prev => [...prev, created]);
            setActiveCategory(created);
            setNewCatName('');
            setAddingCategory(false);
            onToast('success', `"${name}" ${t('added')}`);
        } catch {
            onToast('error', t('failed_to_add_category'));
        }
    };

    const handleSaveCategory = async () => {
        if (!activeCategory) return;
        setSaving(true);
        try {
            const updated = await categoryService.update(activeCategory.id, {
                name: editName.trim(),
                icon: editIcon,
                image_url: editImageUrl,
                is_kitchen_display: editKds,
            });
            setActiveCategory(updated);
            setCategories(prev => prev.map(c => c.id === updated.id ? updated : c));
            onToast('success', t('category_saved'));
        } catch {
            onToast('error', t('failed_to_save_category'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCategory = async () => {
        if (!confirmDelete) return;
        try {
            await categoryService.delete(confirmDelete.id);
            setCategories(prev => prev.filter(c => c.id !== confirmDelete.id));
            if (activeCategory?.id === confirmDelete.id) {
                const remaining = categories.filter(c => c.id !== confirmDelete.id);
                setActiveCategory(remaining.length > 0 ? remaining[0] : null);
            }
            setConfirmDelete(null);
            onToast('success', `"${confirmDelete.name}" ${t('deleted')}`);
        } catch {
            onToast('error', t('failed_to_delete_category'));
            setConfirmDelete(null);
        }
    };

    // ── Subcategory CRUD ─────────────────────────────────────────────────────
    const handleAddSubcategory = async () => {
        const name = newSubName.trim();
        if (!name || !activeCategory) return;
        try {
            const created = await categoryService.createSubcategory({
                name,
                category_id: activeCategory.id,
            });
            setSubcategories(prev => [...prev, created]);
            setNewSubName('');
            setAddingSub(false);
            onToast('success', `"${name}" ${t('added')}`);
        } catch {
            onToast('error', t('failed_to_add_subcategory'));
        }
    };

    const handleSaveSubcategory = async (id) => {
        const name = editSubName.trim();
        if (!name) return;
        try {
            const updated = await categoryService.updateSubcategory(id, { name, image_url: editSubImageUrl });
            setSubcategories(prev => prev.map(s => s.id === id ? updated : s));
            setEditingSubId(null);
            onToast('success', t('subcategory_saved'));
        } catch {
            onToast('error', t('failed_to_save_subcategory'));
        }
    };

    // ── Subcategory reorder ──────────────────────────────────────────────────
    // Same mechanism the POS tile grid and category chips already use
    // (CatalogBrowse.js): move-up/move-down buttons, never drag-and-drop, and
    // the WHOLE new arrangement goes in one catalogueOrderService.reorder
    // call — a half-applied reorder would leave the list in an order nobody
    // chose. `subcategories` is already scoped to the active category (it
    // came from getSubcategories(activeCategory.id)), so the ids sent here
    // can never include another category's subcategories.
    //
    // Unlike a POS tile, a table row here has no tap-to-add gesture the
    // arrows could be confused with, so there is no separate "reorder mode"
    // to enter first — a Portal manager is already authenticated.
    const moveSubcategory = async (subcategoryId, delta) => {
        const from = subcategories.findIndex(s => s.id === subcategoryId);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= subcategories.length) return;
        const reordered = subcategories.slice();
        reordered.splice(to, 0, reordered.splice(from, 1)[0]);
        setSubcategories(reordered);
        try {
            await catalogueOrderService.reorder('subcategories', reordered.map(s => s.id));
        } catch {
            onToast('error', t('failed_to_save_subcategory'));
        }
    };

    const handleDeleteSubcategory = async () => {
        if (!confirmDelete) return;
        try {
            await categoryService.deleteSubcategory(confirmDelete.id);
            setSubcategories(prev => prev.filter(s => s.id !== confirmDelete.id));
            setConfirmDelete(null);
            onToast('success', `"${confirmDelete.name}" ${t('deleted')}`);
        } catch {
            onToast('error', t('failed_to_delete_subcategory'));
            setConfirmDelete(null);
        }
    };

    // ── Translation save handler ─────────────────────────────────────────────
    const handleTranslationSave = async (data) => {
        if (!translateModal) return;
        try {
            if (translateModal.type === 'category') {
                await categoryService.update(translateModal.id, data);
                await fetchCategories();
            } else {
                await categoryService.updateSubcategory(translateModal.id, data);
                // Refresh subcategories
                if (activeCategory) {
                    const subs = await categoryService.getSubcategories(activeCategory.id);
                    setSubcategories(subs);
                }
            }
            setTranslateModal(null);
            onToast('success', t('translation_saved'));
        } catch {
            onToast('error', t('failed_to_save_translation'));
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, paddingTop: 40, textAlign: 'center' }}>
                {t('loading')}...
            </div>
        );
    }

    return (
        <div>
            {/* Modals */}
            {translateModal && (
                <TranslationModal
                    name={translateModal.name}
                    currentMode={translateModal.mode}
                    currentManual={translateModal.manual}
                    onSave={handleTranslationSave}
                    onClose={() => setTranslateModal(null)}
                />
            )}
            {confirmDelete && (
                <ConfirmModal
                    message={`${t('are_you_sure_delete')} "${confirmDelete.name}"?`}
                    onConfirm={confirmDelete.type === 'category' ? handleDeleteCategory : handleDeleteSubcategory}
                    onClose={() => setConfirmDelete(null)}
                />
            )}

            {/* Section Header */}
            <div style={{ marginBottom: 28, paddingBottom: 16, borderBottom: `1.5px solid ${C.border}` }}>
                <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>
                    {t('categories_and_subcategories')}
                </h2>
                <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: '6px 0 0', lineHeight: 1.55 }}>
                    {t('manage_product_categories_description')}
                </p>
            </div>

            {/* Category Pills */}
            <SubLabel mt={0}>{t('categories')}</SubLabel>
            {categories.length === 0 && !addingCategory ? (
                <EmptyState
                    icon={
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" rx="1" />
                            <rect x="14" y="3" width="7" height="7" rx="1" />
                            <rect x="3" y="14" width="7" height="7" rx="1" />
                            <rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>
                    }
                    message={t('no_categories_yet')}
                    cta={`+ ${t('add_first_category')}`}
                    onCta={() => setAddingCategory(true)}
                />
            ) : (
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '7px 14px', borderRadius: 9,
                                    border: `1.5px solid ${activeCategory?.id === cat.id ? C.green : C.border}`,
                                    background: activeCategory?.id === cat.id ? C.greenLight : C.white,
                                    color: activeCategory?.id === cat.id ? C.green : C.text,
                                    fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (activeCategory?.id !== cat.id) e.currentTarget.style.borderColor = C.borderHover; }}
                                onMouseLeave={e => { if (activeCategory?.id !== cat.id) e.currentTarget.style.borderColor = C.border; }}
                            >
                                {cat.image_url
                                    ? renderEntityImage(cat.image_url, 18)
                                    : getCategoryIcon(cat.icon || 'star', { size: 14, color: activeCategory?.id === cat.id ? C.green : C.muted })}
                                {cat.name}
                            </button>
                        ))}

                        {/* Add category inline */}
                        {addingCategory ? (
                            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                <input
                                    value={newCatName}
                                    onChange={e => setNewCatName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') handleAddCategory();
                                        if (e.key === 'Escape') { setAddingCategory(false); setNewCatName(''); }
                                    }}
                                    placeholder={t('category_name')}
                                    autoFocus
                                    style={inp({ width: 160, height: 36, padding: '6px 10px' })}
                                />
                                <button onClick={handleAddCategory} style={{
                                    padding: '6px 12px', borderRadius: 8, border: 'none',
                                    background: C.green, color: C.white,
                                    cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                }}>{t('add')}</button>
                                <button onClick={() => { setAddingCategory(false); setNewCatName(''); }} style={{
                                    padding: '6px 10px', borderRadius: 8,
                                    border: `1.5px solid ${C.border}`, background: C.white,
                                    cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.muted,
                                }}>{t('cancel')}</button>
                            </div>
                        ) : catsFull ? (
                            <LimitReached cap={catCap} requiredPlan="Pro" />
                        ) : (
                            <button
                                onClick={() => setAddingCategory(true)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '7px 14px', borderRadius: 9,
                                    border: `1.5px dashed ${C.greenBorder}`,
                                    background: C.greenLight, color: C.green,
                                    fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                + {t('add_category')}
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* Selected Category Detail */}
            {activeCategory && (
                <div style={{
                    background: C.white, border: `1.5px solid ${C.border}`,
                    borderRadius: 12, padding: 24, marginBottom: 24,
                }}>
                    <SubLabel mt={0}>{t('category_details')}</SubLabel>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.muted, display: 'block', marginBottom: 6 }}>
                            {t('name')}
                        </label>
                        <input
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            style={inp({ maxWidth: 360 })}
                        />
                    </div>

                    {/* Category image/emoji picker */}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.muted, display: 'block', marginBottom: 6 }}>
                            {t('icon_picker_label')}
                        </label>
                        <IconPicker
                            value={editImageUrl}
                            onChange={setEditImageUrl}
                            entityType="category"
                            entityId={activeCategory?.id}
                        />
                    </div>

                    {/* KDS toggle + Translation + Delete row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                        {/* KDS toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <label style={{
                                position: 'relative', display: 'inline-block', width: 38, height: 22, cursor: 'pointer',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={editKds}
                                    onChange={e => setEditKds(e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                />
                                <span style={{
                                    position: 'absolute', inset: 0, borderRadius: 11,
                                    background: editKds ? C.green : '#cbd5e1',
                                    transition: 'background 0.2s',
                                }}>
                                    <span style={{
                                        position: 'absolute', top: 3,
                                        left: editKds ? 18 : 3,
                                        width: 16, height: 16, borderRadius: '50%',
                                        background: C.white,
                                        transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                    }} />
                                </span>
                            </label>
                            <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text }}>
                                {t('kitchen_display')}
                            </span>
                        </div>

                        {/* Translation button */}
                        <button
                            onClick={() => setTranslateModal({
                                type: 'category',
                                id: activeCategory.id,
                                name: activeCategory.name,
                                mode: activeCategory.translation_mode,
                                manual: activeCategory.manual_translation,
                            })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 14px', borderRadius: 8,
                                border: `1.5px solid ${C.border}`, background: C.bg,
                                cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.muted,
                                transition: 'border-color 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHover}
                            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                        >
                            <TranslateIcon size={14} color={C.muted} />
                            {t('translate')}
                            {activeCategory.translation_mode && (
                                <span style={{
                                    fontSize: 10, fontWeight: 700, color: C.green,
                                    background: C.greenLight, padding: '1px 6px', borderRadius: 4,
                                }}>
                                    {activeCategory.translation_mode}
                                </span>
                            )}
                        </button>

                        {/* Delete button */}
                        <button
                            onClick={() => setConfirmDelete({ type: 'category', id: activeCategory.id, name: activeCategory.name })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '6px 14px', borderRadius: 8,
                                border: `1.5px solid ${C.redBorder}`, background: C.redLight,
                                cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.red,
                                marginLeft: 'auto',
                            }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            {t('delete')}
                        </button>
                    </div>

                    {/* Save button */}
                    <button onClick={handleSaveCategory} disabled={saving} style={{
                        padding: '9px 22px', borderRadius: 9, border: 'none',
                        background: C.green, color: C.white,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
                    }}>
                        {saving ? t('saving') + '...' : t('save_changes')}
                    </button>
                </div>
            )}

            {/* Subcategories Table */}
            {activeCategory && (
                <LockOverlay locked={subsLocked} requiredPlan="Pro">
                <div>
                    <SubLabel>{t('subcategories')}</SubLabel>
                    {subLoading ? (
                        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, padding: '16px 0' }}>
                            {t('loading')}...
                        </div>
                    ) : subcategories.length === 0 && !addingSub ? (
                        <div style={{
                            padding: '32px 24px', textAlign: 'center',
                            background: C.bg, borderRadius: 10, border: `1.5px dashed ${C.border}`,
                        }}>
                            <div style={{ fontFamily: FONT, fontSize: 13, color: C.mutedLight, marginBottom: 12 }}>
                                {t('no_subcategories_yet')}
                            </div>
                            <button onClick={() => setAddingSub(true)} style={{
                                padding: '6px 16px', borderRadius: 8, border: `1.5px dashed ${C.greenBorder}`,
                                background: C.greenLight, cursor: 'pointer',
                                fontFamily: FONT, fontSize: 12, fontWeight: 700, color: C.green,
                            }}>
                                + {t('add_subcategory')}
                            </button>
                        </div>
                    ) : isMobile ? (
                        <>
                            <div style={{ marginBottom: 12 }}>
                                {subcategories.map(sub => (
                                    editingSubId === sub.id ? (
                                        <div key={sub.id} style={{
                                            background: C.white, border: `1.5px solid ${C.green}`, borderRadius: 14,
                                            padding: 14, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8,
                                        }}>
                                            <input
                                                value={editSubName}
                                                onChange={e => setEditSubName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleSaveSubcategory(sub.id);
                                                    if (e.key === 'Escape') setEditingSubId(null);
                                                }}
                                                autoFocus
                                                style={inp({})}
                                            />
                                            <IconPicker
                                                value={editSubImageUrl}
                                                onChange={setEditSubImageUrl}
                                                entityType="subcategory"
                                                entityId={sub.id}
                                            />
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button onClick={() => handleSaveSubcategory(sub.id)} style={{
                                                    flex: 1, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                    background: C.green, color: C.white, cursor: 'pointer',
                                                    fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                                }}>{t('save')}</button>
                                                <button onClick={() => setEditingSubId(null)} style={{
                                                    flex: 1, padding: '11px 12px', borderRadius: 8,
                                                    border: `1.5px solid ${C.border}`, background: C.white,
                                                    cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                                                }}>{t('cancel')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <MobileCard
                                            key={sub.id}
                                            avatar={sub.image_url ? undefined : { initials: (sub.name || '?').charAt(0).toUpperCase(), tone: 'gray' }}
                                            title={sub.name}
                                            subtitle={sub.translation_mode ? `${t('translation')}: ${sub.translation_mode}` : (t('no_translation_available') || '—')}
                                        >
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button
                                                    onClick={() => { setEditingSubId(sub.id); setEditSubName(sub.name); setEditSubImageUrl(sub.image_url || null); }}
                                                    style={{ flex: 1, padding: '11px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.bg, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted }}
                                                >{t('edit')}</button>
                                                <button
                                                    onClick={() => setTranslateModal({ type: 'subcategory', id: sub.id, name: sub.name, mode: sub.translation_mode, manual: sub.manual_translation })}
                                                    style={{ flex: 1, padding: '11px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.bg, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                                                >
                                                    <TranslateIcon size={14} color={C.muted} />
                                                    {t('translate')}
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDelete({ type: 'subcategory', id: sub.id, name: sub.name })}
                                                    style={{ padding: '11px 14px', borderRadius: 8, border: `1.5px solid ${C.redBorder}`, background: C.redLight, color: C.red, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                    aria-label={t('delete')}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </MobileCard>
                                    )
                                ))}
                            </div>

                            {/* Add subcategory */}
                            {addingSub ? (
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input
                                        value={newSubName}
                                        onChange={e => setNewSubName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleAddSubcategory();
                                            if (e.key === 'Escape') { setAddingSub(false); setNewSubName(''); }
                                        }}
                                        placeholder={t('subcategory_name')}
                                        autoFocus
                                        style={inp({ height: 40, flex: 1 })}
                                    />
                                    <button onClick={handleAddSubcategory} style={{
                                        padding: '0 16px', height: 40, borderRadius: 8,
                                        background: C.green, color: C.white, border: 'none',
                                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                    }}>{t('add')}</button>
                                    <button onClick={() => { setAddingSub(false); setNewSubName(''); }} style={{
                                        padding: '0 14px', height: 40, borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, background: C.white,
                                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                                    }}>{t('cancel')}</button>
                                </div>
                            ) : (
                                <button onClick={() => setAddingSub(true)} style={{
                                    width: '100%', padding: '12px 18px', borderRadius: 8, border: `1.5px dashed ${C.greenBorder}`,
                                    background: C.greenLight, cursor: 'pointer', fontFamily: FONT,
                                    fontSize: 13, fontWeight: 700, color: C.green,
                                }}>
                                    + {t('add_subcategory')}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{
                                border: `1.5px solid ${C.border}`, borderRadius: 10,
                                overflow: 'hidden', marginBottom: 12,
                            }}>
                                <div className="portal-table-wrap">
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: C.bg }}>
                                            <th style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, padding: '9px 16px', textAlign: 'left' }}>
                                                {t('name')}
                                            </th>
                                            <th style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, padding: '9px 16px', textAlign: 'center' }}>
                                                {t('translation')}
                                            </th>
                                            <th style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, padding: '9px 16px', textAlign: 'center', width: 170 }}>
                                                {t('actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subcategories.map((sub, i) => (
                                            <tr
                                                key={sub.id}
                                                style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}
                                                onMouseEnter={e => e.currentTarget.style.background = C.bg}
                                                onMouseLeave={e => e.currentTarget.style.background = C.white}
                                            >
                                                <td style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text, padding: '10px 16px' }}>
                                                    {editingSubId === sub.id ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                                <input
                                                                    value={editSubName}
                                                                    onChange={e => setEditSubName(e.target.value)}
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') handleSaveSubcategory(sub.id);
                                                                        if (e.key === 'Escape') setEditingSubId(null);
                                                                    }}
                                                                    autoFocus
                                                                    style={inp({ width: 200, height: 32, padding: '4px 10px' })}
                                                                />
                                                                <button onClick={() => handleSaveSubcategory(sub.id)} style={{
                                                                    padding: '4px 10px', borderRadius: 6, border: 'none',
                                                                    background: C.green, color: C.white, cursor: 'pointer',
                                                                    fontFamily: FONT, fontSize: 11, fontWeight: 700,
                                                                }}>{t('save')}</button>
                                                                <button onClick={() => setEditingSubId(null)} style={{
                                                                    padding: '4px 8px', borderRadius: 6,
                                                                    border: `1.5px solid ${C.border}`, background: C.white,
                                                                    cursor: 'pointer', fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted,
                                                                }}>{t('cancel')}</button>
                                                            </div>
                                                            <IconPicker
                                                                value={editSubImageUrl}
                                                                onChange={setEditSubImageUrl}
                                                                entityType="subcategory"
                                                                entityId={sub.id}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {sub.image_url && renderEntityImage(sub.image_url, 22)}
                                                            {sub.name}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                                                    <button
                                                        onClick={() => setTranslateModal({
                                                            type: 'subcategory',
                                                            id: sub.id,
                                                            name: sub.name,
                                                            mode: sub.translation_mode,
                                                            manual: sub.manual_translation,
                                                        })}
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                                            padding: '4px 10px', borderRadius: 6,
                                                            border: `1.5px solid ${C.border}`, background: C.bg,
                                                            cursor: 'pointer', fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted,
                                                        }}
                                                    >
                                                        <TranslateIcon size={12} color={C.muted} />
                                                        {sub.translation_mode ? (
                                                            <span style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>
                                                                {sub.translation_mode}
                                                            </span>
                                                        ) : t('translate')}
                                                    </button>
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                                                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                        <button
                                                            type="button"
                                                            data-testid={`subcategory-move-up-${sub.id}`}
                                                            aria-label={`${sub.name}: ${t('move_up', {}, 'Move up')}`}
                                                            disabled={i === 0}
                                                            onClick={() => moveSubcategory(sub.id, -1)}
                                                            style={{
                                                                padding: '3px 7px', borderRadius: 6,
                                                                border: `1.5px solid ${C.border}`, background: C.bg,
                                                                cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.4 : 1,
                                                                fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted,
                                                            }}
                                                        >&#8593;</button>
                                                        <button
                                                            type="button"
                                                            data-testid={`subcategory-move-down-${sub.id}`}
                                                            aria-label={`${sub.name}: ${t('move_down', {}, 'Move down')}`}
                                                            disabled={i === subcategories.length - 1}
                                                            onClick={() => moveSubcategory(sub.id, 1)}
                                                            style={{
                                                                padding: '3px 7px', borderRadius: 6,
                                                                border: `1.5px solid ${C.border}`, background: C.bg,
                                                                cursor: i === subcategories.length - 1 ? 'default' : 'pointer',
                                                                opacity: i === subcategories.length - 1 ? 0.4 : 1,
                                                                fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted,
                                                            }}
                                                        >&#8595;</button>
                                                        <button
                                                            onClick={() => { setEditingSubId(sub.id); setEditSubName(sub.name); setEditSubImageUrl(sub.image_url || null); }}
                                                            style={{
                                                                padding: '3px 8px', borderRadius: 6,
                                                                border: `1.5px solid ${C.border}`, background: C.bg,
                                                                cursor: 'pointer', fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted,
                                                            }}
                                                        >
                                                            {t('edit')}
                                                        </button>
                                                        <DelBtn onClick={() => setConfirmDelete({ type: 'subcategory', id: sub.id, name: sub.name })} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </div>

                            {/* Add subcategory */}
                            {addingSub ? (
                                <div style={{ display: 'flex', gap: 8, maxWidth: 400 }}>
                                    <input
                                        value={newSubName}
                                        onChange={e => setNewSubName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleAddSubcategory();
                                            if (e.key === 'Escape') { setAddingSub(false); setNewSubName(''); }
                                        }}
                                        placeholder={t('subcategory_name')}
                                        autoFocus
                                        style={inp({ height: 36, flex: 1 })}
                                    />
                                    <button onClick={handleAddSubcategory} style={{
                                        padding: '0 14px', height: 36, borderRadius: 8,
                                        background: C.green, color: C.white, border: 'none',
                                        cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                    }}>{t('add')}</button>
                                    <button onClick={() => { setAddingSub(false); setNewSubName(''); }} style={{
                                        padding: '0 12px', height: 36, borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, background: C.white,
                                        cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.muted,
                                    }}>{t('cancel')}</button>
                                </div>
                            ) : (
                                <button onClick={() => setAddingSub(true)} style={{
                                    padding: '8px 18px', borderRadius: 8, border: `1.5px dashed ${C.greenBorder}`,
                                    background: C.greenLight, cursor: 'pointer', fontFamily: FONT,
                                    fontSize: 13, fontWeight: 700, color: C.green,
                                }}>
                                    + {t('add_subcategory')}
                                </button>
                            )}
                        </>
                    )}
                </div>
                </LockOverlay>
            )}
        </div>
    );
};

export default CataloguesSection;
